import type { ReactNode } from "react";

import type { ReportTemplate } from "../../hooks/useTemplate";
import { isDraftFromDifferentTemplate } from "../../utils/reportUtils";

interface ReportTemplateGateValue {
  template: ReportTemplate | undefined;
  templateMismatchDraftDetected: boolean;
}

interface ReportTemplateGateProps {
  isSigned: boolean;
  examType: string | null;
  template: ReportTemplate | undefined;
  isLoading: boolean;
  error: unknown;
  savedEditorState: unknown;
  savedExamType: string | null | undefined;
  loadingFallback: ReactNode;
  errorFallback: (error: unknown) => ReactNode;
  unavailableFallback: ReactNode;
  children: (value: ReportTemplateGateValue) => ReactNode;
}

/**
 * Prevent the editor from mounting until the backend-selected report template
 * has resolved. This boundary is intentionally outside the TipTap component:
 * a saved draft must never get one render against an unresolved (or wrong)
 * anatomy before template-mismatch detection can run.
 */
export function ReportTemplateGate({
  isSigned,
  examType,
  template,
  isLoading,
  error,
  savedEditorState,
  savedExamType,
  loadingFallback,
  errorFallback,
  unavailableFallback,
  children,
}: ReportTemplateGateProps) {
  if (!isSigned && isLoading) return loadingFallback;
  if (!isSigned && error) return errorFallback(error);
  if (!isSigned && (!examType || !template)) return unavailableFallback;

  const templateMismatchDraftDetected =
    !isSigned &&
    !!examType &&
    isDraftFromDifferentTemplate(savedEditorState, savedExamType, template);

  return children({ template, templateMismatchDraftDetected });
}
